import { useLanguage } from '../i18n';

type ErrorCode = 401 | 402 | 404 | 429 | 500 | 505;

interface ErrorPageProps {
  code: ErrorCode;
  onGoHome?: () => void;
  onRetry?: () => void;
}

// 에러별 아이콘 및 색상 테마
const ERROR_THEMES: Record<ErrorCode, { icon: string; bg: string; accent: string }> = {
  401: { icon: '🔒', bg: 'var(--neo-mint)', accent: 'var(--neo-blue)' },
  402: { icon: '💳', bg: 'var(--neo-yellow)', accent: 'var(--neo-orange)' },
  404: { icon: '🔍', bg: 'var(--neo-pink)', accent: 'var(--neo-blue)' },
  429: { icon: '⏳', bg: 'var(--neo-orange)', accent: 'var(--neo-yellow)' },
  500: { icon: '💥', bg: 'var(--neo-pink)', accent: 'var(--neo-lime)' },
  505: { icon: '🔌', bg: 'var(--neo-blue)', accent: 'var(--neo-mint)' },
};

export default function ErrorPage({ code, onGoHome, onRetry }: ErrorPageProps) {
  const { t } = useLanguage();
  const theme = ERROR_THEMES[code];
  const errorText = t.errorPage?.[code];

  // 홈으로 이동
  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  // 다시 시도
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleLogin = () => {
    // 홈으로 이동 후 LoginButton 클릭 트리거
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    setTimeout(() => {
      const loginBtn = document.querySelector('[data-testid="login-button"]') as HTMLButtonElement | null;
      loginBtn?.click();
    }, 0);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--bg-primary)]">
      <div className="max-w-lg w-full">
        {/* 메인 에러 카드 */}
        <div 
          className="neo-card-static p-8 text-center"
          style={{ backgroundColor: theme.bg }}
        >
          {/* 에러 코드 배지 */}
          <div className="inline-flex items-center justify-center mb-6">
            <span 
              className="neo-badge text-4xl px-6 py-3 bg-black text-white"
              style={{ fontFamily: 'Archivo Black, Impact, sans-serif' }}
            >
              {code}
            </span>
          </div>

          {/* 아이콘 */}
          <div className="mb-6">
            <div 
              className="inline-flex items-center justify-center w-24 h-24 text-6xl bg-white border-4 border-black"
              style={{ boxShadow: '4px 4px 0 black' }}
            >
              {theme.icon}
            </div>
          </div>

          {/* 에러 타이틀 */}
          <h1 
            className="text-2xl md:text-3xl font-black text-black mb-3 uppercase tracking-tight"
            style={{ fontFamily: 'Archivo Black, Impact, sans-serif' }}
          >
            {errorText?.title || 'Error'}
          </h1>

          {/* 설명 */}
          <p className="text-black/80 text-sm md:text-base font-bold mb-8 leading-relaxed">
            {errorText?.description || 'An error occurred.'}
          </p>

          {/* 힌트 박스 */}
          {errorText?.hint && (
            <div 
              className="mb-4 p-4 bg-white/50 border-3 border-black text-left"
              style={{ borderWidth: '3px' }}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl">💡</span>
                <p className="text-sm font-bold text-black/70">
                  {errorText.hint}
                </p>
              </div>
            </div>
          )}

          {/* 지원 연락처 안내 - 서버 에러(500, 505)에서만 표시 */}
          {(code === 500 || code === 505) && t.errorPage?.supportContact && (
            <div className="mb-8 text-center">
              <p className="text-sm text-black/60">
                {t.errorPage.supportContact.split('{email}')[0]}
                <a 
                  href="mailto:support@releasegate.app"
                  className="inline-flex items-center gap-1 font-bold text-black underline underline-offset-2 hover:text-black/80 transition-colors"
                >
                  ✉️ support@releasegate.app
                </a>
                {t.errorPage.supportContact.split('{email}')[1]}
              </p>
            </div>
          )}

          {/* 액션 버튼들 */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleGoHome}
              className="neo-btn px-6 py-3 bg-black text-white hover:bg-gray-800"
            >
              🏠 {t.errorPage?.goHome || 'Go Home'}
            </button>

            {code === 401 && (
              <button
                onClick={handleLogin}
                className="neo-btn px-6 py-3 bg-white text-black hover:bg-gray-100"
              >
                🔐 {t.auth.login || 'Login'}
              </button>
            )}
            
            {(code === 429 || code === 500 || code === 505) && (
              <button
                onClick={handleRetry}
                className="neo-btn px-6 py-3 bg-white text-black hover:bg-gray-100"
              >
                🔄 {t.errorPage?.retry || 'Retry'}
              </button>
            )}

            {code === 402 && (
              <button
                onClick={() => window.location.href = '/?pricing=open'}
                className="neo-btn px-6 py-3 text-black"
                style={{ backgroundColor: theme.accent }}
              >
                💎 {t.errorPage?.viewPricing || 'View Pricing'}
              </button>
            )}
          </div>
        </div>

        {/* 하단 데코 요소 */}
        <div className="mt-6 flex justify-center gap-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-3 h-3 bg-black dark:bg-white/30"
              style={{ 
                transform: `rotate(${i * 15}deg)`,
                opacity: 0.2 + (i * 0.15)
              }}
            />
          ))}
        </div>

        {/* 브랜드 */}
        <div className="mt-6 text-center">
          <span className="text-xs font-bold text-terminal-muted uppercase tracking-wider">
            ReleaseGate
          </span>
        </div>
      </div>
    </div>
  );
}

// 개별 에러 페이지 export
export function Error402Page(props: Omit<ErrorPageProps, 'code'>) {
  return <ErrorPage code={402} {...props} />;
}

export function Error404Page(props: Omit<ErrorPageProps, 'code'>) {
  return <ErrorPage code={404} {...props} />;
}

export function Error429Page(props: Omit<ErrorPageProps, 'code'>) {
  return <ErrorPage code={429} {...props} />;
}

export function Error500Page(props: Omit<ErrorPageProps, 'code'>) {
  return <ErrorPage code={500} {...props} />;
}

export function Error505Page(props: Omit<ErrorPageProps, 'code'>) {
  return <ErrorPage code={505} {...props} />;
}

